
import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit, 
  doc, 
  setDoc, 
  getDoc 
} from 'firebase/firestore';
import { SavedResult, UserSettings, Language, TypingMode } from '../types';

export const saveUserResult = async (uid: string, result: SavedResult) => {
  try {
    await addDoc(collection(db, 'results'), {
      ...result,
      uid
    });
  } catch (e) {
    console.error("Error saving result:", e);
  }
};

export const getUserHistory = async (uid: string): Promise<SavedResult[]> => {
  try {
    const q = query(
      collection(db, 'results'), 
      where('uid', '==', uid),
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as SavedResult));
  } catch (e: any) {
    // Fallback if composite index is missing
    if (e.code === 'failed-precondition' || e.message?.includes('index')) {
        console.warn("History Index missing, falling back to unsorted fetch");
         const qFallback = query(
          collection(db, 'results'), 
          where('uid', '==', uid),
          limit(50)
        );
        const snapshot = await getDocs(qFallback);
        const data = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as SavedResult));
        return data.sort((a,b) => b.timestamp - a.timestamp);
    }
    console.error("Firestore History Error:", e);
    return [];
  }
};

export const getGlobalLeaderboard = async (mode: TypingMode, config: number): Promise<SavedResult[]> => {
  try {
    const q = query(
      collection(db, 'results'),
      where('mode', '==', mode),
      where('config', '==', config),
      orderBy('stats.wpm', 'desc'),
      limit(50)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as SavedResult));
  } catch (e: any) {
    // Handle missing index gracefully by falling back to client-side sorting
    if (e.code === 'failed-precondition' || e.message?.includes('index')) {
        console.warn("Leaderboard Index missing. Falling back to client-side sort.");
        try {
            // Fallback: Fetch results without sorting from DB, then sort in memory
            const qFallback = query(
                collection(db, 'results'),
                where('mode', '==', mode),
                where('config', '==', config),
                limit(100)
            );
            const snapshot = await getDocs(qFallback);
            const data = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as SavedResult));
            return data.sort((a, b) => b.stats.wpm - a.stats.wpm).slice(0, 50);
        } catch (fallbackError) {
             console.error("Leaderboard Fallback Error:", fallbackError);
             return [];
        }
    }
    console.error("Leaderboard Fetch Error:", e);
    // Note: This query requires a composite index on (mode, config, stats.wpm DESC)
    return [];
  }
};

export const saveUserSettings = async (uid: string, settings: UserSettings) => {
  try {
    await setDoc(doc(db, 'settings', uid), settings);
  } catch (e) {
    console.error("Error saving settings:", e);
  }
};

export const getUserSettings = async (uid: string): Promise<UserSettings | null> => {
  try {
    const d = await getDoc(doc(db, 'settings', uid));
    return d.exists() ? d.data() as UserSettings : null;
  } catch (e) {
    console.error("Error fetching settings:", e);
    return null;
  }
};

export const getLibraryWords = async (language: Language): Promise<string[]> => {
  try {
    const d = await getDoc(doc(db, 'library', language));
    if (d.exists()) return d.data().words;
  } catch (e) {
    console.error("Error fetching library:", e);
  }
  return [];
};
